/**
 * Scenario validation.
 *
 * The contribution surface of this project is a JSON file. That means the error message a
 * contributor gets when they mistype a field is part of the product — someone who has just
 * spent an evening assembling employment statistics for their country deserves better than
 * `Unexpected token }`. Every message here says which scenario, which sector, which field,
 * and what was expected.
 *
 * Validation is deliberately strict about structure and permissive about values. Unknown
 * keys are rejected, because a silently ignored `bufferdays` is a contributor wondering for
 * an hour why their change does nothing. Ranges are enforced where a value outside them is
 * meaningless (a share above 1), but not where it is merely unusual — a scenario with a
 * 300-day strike fund is someone asking a legitimate what-if, not a mistake.
 */

import { z } from 'zod';
import type { Scenario } from './types.ts';

/** A share of some population or total. */
const share = z.number().min(0).max(1);
/** A dimensionless 0-1 weight. */
const index = z.number().min(0).max(1);

const sourceRefSchema = z
  .object({
    title: z.string().min(1, 'A source needs a title.'),
    ref: z.string().min(1, 'A source needs a reference — a URL, DOI, or full citation.'),
    supports: z
      .string()
      .min(1, 'Say which parameters this source informs, so a reader can check it against the right number.'),
  })
  .strict();

const sectorSchema = z
  .object({
    id: z
      .string()
      .regex(/^[a-z0-9-]+$/, 'Sector ids must be lowercase letters, digits and hyphens.')
      .min(1),
    name: z.string().min(1),
    size: share,
    bufferDays: z.number().min(0).max(3650),
    bufferSpread: z.number().min(0).max(3),
    riskTolerance: index,
    leverage: index,
    networkDensity: index,
    visibility: index,
    threshold: z
      .object({
        mean: share,
        sd: z.number().min(0).max(1),
      })
      .strict(),
    initialPrivateSupport: share,
    initialParticipation: share,
    essential: z.boolean(),
    inputDependence: index,
  })
  .strict()
  .refine((s) => s.initialParticipation <= s.initialPrivateSupport, {
    message:
      'More of this sector is on strike at day 0 than privately supports the movement. Nobody strikes for a cause they do not support — raise initialPrivateSupport or lower initialParticipation.',
    path: ['initialParticipation'],
  });

const pillarSchema = z
  .object({
    id: z
      .string()
      .regex(/^[a-z0-9-]+$/, 'Pillar ids must be lowercase letters, digits and hyphens.')
      .min(1),
    name: z.string().min(1),
    loyalty: index,
    weight: z.number().min(0).max(1),
    economicExposure: index,
    breadthSensitivity: index,
    repressionSensitivity: index,
    cascadeInfluence: index,
    linkedSectors: z.array(z.string()),
  })
  .strict();

const movementSchema = z
  .object({
    nonviolentDiscipline: index,
    strikeFundDays: z.number().min(0).max(3650),
    organisation: index,
  })
  .strict();

const mediaSchema = z
  .object({
    stateControl: index,
    independentReach: index,
  })
  .strict();

const regimeSchema = z
  .object({
    initialRepression: index,
    repressionCapacity: index,
    initialLegitimacy: index,
    initialTreasury: index,
    intransigence: index,
  })
  .strict();

export const scenarioSchema = z
  .object({
    id: z
      .string()
      .regex(/^[a-z0-9-]+$/, 'Scenario ids must be lowercase letters, digits and hyphens, matching the filename.')
      .min(1),
    name: z.string().min(1),
    premise: z
      .string()
      .min(60, 'Describe the situation properly — a reader needs to know what is being fought over before any number means anything.'),
    demand: z.string().min(1, 'State what the movement is asking for. It appears in the feed and on the outcome poster.'),
    confidence: z.enum(['high', 'medium', 'low']),
    provenance: z
      .string()
      .min(80, 'Say where these numbers came from and what they are not. This is the field that keeps the project honest.'),
    sources: z.array(sourceRefSchema).min(1, 'Cite at least one source. A scenario with no sources is a guess wearing a name badge.'),
    horizonDays: z.number().int().min(1).max(3650),
    sectors: z.array(sectorSchema).min(2, 'A scenario needs at least two sectors — the model is about the relationship between them.'),
    pillars: z.array(pillarSchema).min(1, 'A regime rests on something. Declare at least one pillar of support.'),
    movement: movementSchema,
    media: mediaSchema,
    regime: regimeSchema,
  })
  .strict()
  .superRefine((scenario, ctx) => {
    const sectorIds = new Set<string>();
    scenario.sectors.forEach((sector, i) => {
      if (sectorIds.has(sector.id)) {
        ctx.addIssue({
          code: 'custom',
          path: ['sectors', i, 'id'],
          message: `Duplicate sector id "${sector.id}". Ids must be unique — they seed each sector's random stream and drive the canonical ordering.`,
        });
      }
      sectorIds.add(sector.id);
    });

    const pillarIds = new Set<string>();
    scenario.pillars.forEach((pillar, i) => {
      if (pillarIds.has(pillar.id)) {
        ctx.addIssue({
          code: 'custom',
          path: ['pillars', i, 'id'],
          message: `Duplicate pillar id "${pillar.id}".`,
        });
      }
      pillarIds.add(pillar.id);

      pillar.linkedSectors.forEach((linked, j) => {
        if (!sectorIds.has(linked)) {
          ctx.addIssue({
            code: 'custom',
            path: ['pillars', i, 'linkedSectors', j],
            message: `Pillar "${pillar.id}" is linked to sector "${linked}", which this scenario does not define. Available: ${[...sectorIds].join(', ')}.`,
          });
        }
      });
    });

    const totalSize = scenario.sectors.reduce((acc, s) => acc + s.size, 0);
    if (totalSize <= 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['sectors'],
        message: 'Sector sizes sum to zero — this scenario has no workforce.',
      });
    }
    // Sizes are renormalised by the engine, so this is advice rather than an error. Being
    // far off usually means a sector was forgotten rather than that shares were rescaled.
    if (totalSize > 0 && (totalSize < 0.5 || totalSize > 1.5)) {
      ctx.addIssue({
        code: 'custom',
        path: ['sectors'],
        message: `Sector sizes sum to ${totalSize.toFixed(2)} rather than about 1. They will be renormalised, but a total this far off usually means a sector is missing or double-counted.`,
      });
    }

    const totalWeight = scenario.pillars.reduce((acc, p) => acc + p.weight, 0);
    if (totalWeight <= 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['pillars'],
        message: 'Pillar weights sum to zero — this regime rests on nothing and cannot be modelled.',
      });
    }

    if (!scenario.sectors.some((s) => s.essential)) {
      ctx.addIssue({
        code: 'custom',
        path: ['sectors'],
        message:
          'No sector is marked essential, so this economy can never produce a shortage and the public never feels the strike. That is a legitimate scenario, but it is usually an oversight — healthcare, energy, food retail and transport are the usual candidates.',
      });
    }
  });

export type ValidatedScenario = z.infer<typeof scenarioSchema>;

export interface ValidationIssue {
  /** Dotted path to the offending field, e.g. `sectors.3.bufferDays`. */
  path: string;
  message: string;
}

export type ValidationResult =
  | { ok: true; scenario: Scenario }
  | { ok: false; issues: ValidationIssue[] };

/**
 * Validate a parsed JSON value as a scenario.
 *
 * Returns issues rather than throwing, so a loader can report every problem in a file at
 * once instead of making a contributor fix them one run at a time.
 */
export function validateScenario(value: unknown): ValidationResult {
  const result = scenarioSchema.safeParse(value);
  if (result.success) return { ok: true, scenario: result.data as Scenario };
  return {
    ok: false,
    issues: result.error.issues.map((issue) => ({
      path: issue.path.join('.') || '(root)',
      message: issue.message,
    })),
  };
}

/** Validate, or throw with every problem listed in a form a person can act on. */
export function parseScenario(value: unknown, label = 'scenario'): Scenario {
  const result = validateScenario(value);
  if (result.ok) return result.scenario;
  const lines = result.issues.map((i) => `  ${i.path}: ${i.message}`);
  throw new Error(`${label} is not valid:\n${lines.join('\n')}`);
}

/**
 * JSON Schema for `data/scenarios/*.json`, so editors can offer completion and inline
 * validation while a contributor types. Generated from the zod schema above rather than
 * maintained separately, because two schemas always drift.
 */
export function scenarioJsonSchema(): unknown {
  return z.toJSONSchema(scenarioSchema, { io: 'input' });
}
